import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill, SITE_LABEL } from "@/components/Layout";
import TimeInput24 from "@/components/TimeInput24";
import { toast } from "sonner";
import { Download, Pencil, CheckCircle, ExternalLink, Trash2, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";

// ----- Week helpers (Mon–Sun, NZ-local) -----
function isoDate(d) {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function formatWeekLabel(monday) {
  const sun = addDays(monday, 6);
  const fmt = (d) =>
    d.toLocaleDateString([], { day: "numeric", month: "short" });
  return `${fmt(monday)} – ${fmt(sun)} ${sun.getFullYear()}`;
}

function EditDialog({ shift, onSaved }) {
  const [open, setOpen] = useState(false);
  // Split the stored UTC timestamps into a single shift date + two 24-hour
  // time strings. The user is in NZ, so use local components (Date object's
  // toLocaleDateString() and toTimeString() yield browser-local values, which
  // for a NZ admin is Pacific/Auckland).
  const toLocalDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  };
  const toLocalTime = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(11, 16);
  };
  const [form, setForm] = useState({
    shift_date: toLocalDate(shift.clock_in_ts),
    clock_in_time: toLocalTime(shift.clock_in_ts),
    clock_out_time: toLocalTime(shift.clock_out_ts),
    status: shift.status,
    admin_note: shift.admin_note || "",
    break_minutes:
      shift.break_minutes === null || shift.break_minutes === undefined
        ? ""
        : String(shift.break_minutes),
    break_start_time: toLocalTime(shift.break_start_ts),
    break_end_time: toLocalTime(shift.break_end_ts),
    reason: "",
  });

  // If clock-out time is BEFORE clock-in time, the shift spans midnight ⇒
  // clock-out is on the following day.
  const overnight =
    form.clock_in_time &&
    form.clock_out_time &&
    form.clock_out_time < form.clock_in_time;

  const combine = (dateStr, timeStr, addDays = 0) => {
    if (!dateStr || !timeStr) return null;
    const [Y, M, D] = dateStr.split("-").map(Number);
    const [h, m] = timeStr.split(":").map(Number);
    return new Date(Y, M - 1, D + addDays, h, m, 0, 0);
  };

  // Break start/end are optional. They sit inside the shift, so if the time
  // is earlier than clock-in the break falls on the next calendar day
  // (overnight shift). If end lands before start, roll it forward a day too.
  const breakStart = form.break_start_time
    ? combine(form.shift_date, form.break_start_time, form.break_start_time < form.clock_in_time ? 1 : 0)
    : null;
  let breakEnd = form.break_end_time
    ? combine(form.shift_date, form.break_end_time, form.break_end_time < form.clock_in_time ? 1 : 0)
    : null;
  if (breakStart && breakEnd && breakEnd <= breakStart) {
    breakEnd = new Date(breakEnd.getTime() + 24 * 60 * 60 * 1000);
  }

  // Live preview of the recalculated paid hours.
  const previewIn = combine(form.shift_date, form.clock_in_time, 0);
  const previewOut = form.clock_out_time
    ? combine(form.shift_date, form.clock_out_time, overnight ? 1 : 0)
    : null;
  const grossMins = previewIn && previewOut
    ? Math.max(0, Math.round((previewOut - previewIn) / 60000))
    : null;
  let previewBreak = null;
  if (breakStart && breakEnd) {
    previewBreak = Math.max(0, Math.round((breakEnd - breakStart) / 60000));
  } else if (form.break_minutes !== "" && Number.isFinite(Number(form.break_minutes))) {
    previewBreak = Math.max(0, Math.round(Number(form.break_minutes)));
  } else if (grossMins !== null) {
    previewBreak = grossMins > 360 ? 30 : 0; // auto 30-min deduction on 6h+
  }
  const previewNet = grossMins !== null ? Math.max(0, grossMins - (previewBreak || 0)) : null;

  const submit = async () => {
    if (!form.reason) { toast.error("Reason is required for audit log"); return; }
    const clockIn = combine(form.shift_date, form.clock_in_time, 0);
    const clockOut = form.clock_out_time
      ? combine(form.shift_date, form.clock_out_time, overnight ? 1 : 0)
      : null;
    const payload = {
      clock_in: clockIn ? clockIn.toISOString() : null,
      clock_out: clockOut ? clockOut.toISOString() : null,
      status: form.status,
      admin_note: form.admin_note,
      reason: form.reason,
    };
    if (breakStart && breakEnd) {
      payload.break_start = breakStart.toISOString();
      payload.break_end = breakEnd.toISOString();
    } else if (form.break_minutes !== "") {
      const mins = Number(form.break_minutes);
      if (!Number.isFinite(mins) || mins < 0) { toast.error("Break minutes must be 0 or more"); return; }
      payload.break_minutes = Math.round(mins);
      payload.clear_break_times = true;
    }
    try {
      await api.patch(`/admin/shifts/${shift.id}`, payload);
      toast.success("Shift updated");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`edit-shift-${shift.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit shift</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Shift date</Label>
            <Input
              type="date"
              value={form.shift_date}
              onChange={(e) => setForm({ ...form, shift_date: e.target.value })}
              data-testid="edit-shift-date"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Clock in (24h)</Label>
              <TimeInput24
                value={form.clock_in_time}
                onChange={(v) => setForm({ ...form, clock_in_time: v })}
                data-testid="edit-clockin-time"
              />
            </div>
            <div>
              <Label>Clock out (24h)</Label>
              <TimeInput24
                value={form.clock_out_time}
                onChange={(v) => setForm({ ...form, clock_out_time: v })}
                data-testid="edit-clockout-time"
              />
            </div>
          </div>
          {overnight && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              Clock-out is before clock-in — treating this as an overnight shift; clock-out will be saved as the next day.
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Break</Label>
              {shift.break_auto_closed && (
                <span className="text-[11px] text-amber-700" data-testid="break-autoclosed-note">
                  Break was auto-closed at clock-out
                </span>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-600">Total break (minutes)</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  step="5"
                  className="w-28"
                  value={form.break_minutes}
                  placeholder="auto"
                  onChange={(e) => setForm({ ...form, break_minutes: e.target.value })}
                  data-testid="edit-break-minutes"
                />
                {[0, 15, 30, 45, 60].map((m) => (
                  <Button
                    key={m}
                    type="button"
                    size="sm"
                    variant="outline"
                    className="px-2"
                    onClick={() => setForm({ ...form, break_minutes: String(m), break_start_time: "", break_end_time: "" })}
                    data-testid={`edit-break-preset-${m}`}
                  >
                    {m === 0 ? "No break" : `${m}m`}
                  </Button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">
                Setting a value here overrides the automatic 30-min deduction on shifts over 6 hours. Use “No break” when no break was taken.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Break start (optional)</Label>
                <TimeInput24
                  value={form.break_start_time}
                  onChange={(v) => setForm({ ...form, break_start_time: v })}
                  data-testid="edit-break-start"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Break end (optional)</Label>
                <TimeInput24
                  value={form.break_end_time}
                  onChange={(v) => setForm({ ...form, break_end_time: v })}
                  data-testid="edit-break-end"
                />
              </div>
            </div>
            {breakStart && breakEnd && (
              <p className="text-[11px] text-slate-600">
                Break times take priority — {Math.round((breakEnd - breakStart) / 60000)} min will be deducted.
              </p>
            )}
            {(form.break_start_time || form.break_end_time) && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => setForm({ ...form, break_start_time: "", break_end_time: "" })}
                data-testid="edit-break-clear-times"
              >
                Clear break times
              </Button>
            )}
            {previewNet !== null && (
              <div className="rounded-md bg-white border border-slate-200 px-3 py-2 text-xs text-slate-700" data-testid="edit-hours-preview">
                Gross {(grossMins / 60).toFixed(2)} hrs − break {previewBreak || 0} min ={" "}
                <span className="font-semibold">{(previewNet / 60).toFixed(2)} hrs paid</span>
              </div>
            )}
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["ACTIVE", "COMPLETE", "APPROVED", "INCOMPLETE", "FLAGGED", "DISPUTED"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Admin note</Label><Input value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })} data-testid="edit-admin-note" /></div>
          <div><Label>Reason (required — written to audit log)</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Employee forgot to clock out" data-testid="edit-reason" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-[#1A2744] text-white" data-testid="edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ shift, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setReason(""); setBusy(false); };
  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) { toast.error("Reason is required for audit log"); return; }
    setBusy(true);
    try {
      try {
        await api.delete(`/admin/shifts/${shift.id}`, { data: { reason: trimmed } });
      } catch (err) {
        // Some proxies (Cloudflare WAF rules, corporate gateways) block the
        // DELETE HTTP method and return 405 Method Not Allowed before the
        // request reaches the backend. Fall back to the POST shape, which
        // executes the same handler server-side.
        const status = err?.response?.status;
        if (status === 405 || status === 501) {
          await api.post(`/admin/shifts/${shift.id}/delete`, { reason: trimmed });
        } else {
          throw err;
        }
      }
      toast.success("Timesheet entry deleted");
      setOpen(false);
      reset();
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete this entry.");
    } finally {
      setBusy(false);
    }
  };

  // Format clock-in for the confirmation copy so admins see exactly which row
  // is being removed before they commit.
  const when = shift.clock_in_ts
    ? new Date(shift.clock_in_ts).toLocaleString([], {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit", hour12: false,
      })
    : "—";

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
          data-testid={`delete-shift-${shift.id}`}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-700">Delete this timesheet entry?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-red-900">
            <div className="font-semibold">{shift.user_name || "Employee"} — {when}</div>
            <div className="text-xs mt-0.5 opacity-80">
              {shift.total_minutes ? `${(shift.total_minutes / 60).toFixed(2)} hrs` : "—"}
              {" · "}Status: {shift.status}
            </div>
            <div className="text-xs mt-2">
              This is permanent. The row is removed from the database, but a full snapshot is kept in the audit log so it can be reviewed later if needed.
            </div>
          </div>
          {shift.xero_ref && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-amber-900 text-xs">
              ⚠️ This shift has been pushed to Xero (ref <code>{shift.xero_ref}</code>). You must reverse it in Xero first — the API will refuse this delete otherwise.
            </div>
          )}
          <div>
            <Label>Reason (required — written to audit log)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate clock-in caused by phone error"
              data-testid={`delete-shift-${shift.id}-reason`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid={`delete-shift-${shift.id}-cancel`}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !reason.trim()}
            className="bg-red-600 text-white hover:bg-red-700"
            data-testid={`delete-shift-${shift.id}-confirm`}
          >
            <Trash2 className="h-4 w-4 mr-1" /> {busy ? "Deleting…" : "Delete entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddShiftDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const blank = {
    user_id: "",
    site: "",
    shift_date: isoDate(new Date()),
    clock_in_time: "",
    clock_out_time: "",
    break_minutes: "",
    admin_note: "",
    reason: "",
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});
  }, [open]);

  const combine = (dateStr, timeStr, plusDays = 0) => {
    if (!dateStr || !timeStr) return null;
    const [Y, M, D] = dateStr.split("-").map(Number);
    const [h, m] = timeStr.split(":").map(Number);
    return new Date(Y, M - 1, D + plusDays, h, m, 0, 0);
  };

  const overnight =
    form.clock_in_time && form.clock_out_time && form.clock_out_time < form.clock_in_time;
  const cIn = combine(form.shift_date, form.clock_in_time, 0);
  const cOut = form.clock_out_time ? combine(form.shift_date, form.clock_out_time, overnight ? 1 : 0) : null;
  const grossMins = cIn && cOut ? Math.max(0, Math.round((cOut - cIn) / 60000)) : null;
  const brkMins =
    form.break_minutes !== "" && Number.isFinite(Number(form.break_minutes))
      ? Math.max(0, Math.round(Number(form.break_minutes)))
      : grossMins !== null
        ? (grossMins > 360 ? 30 : 0)
        : 0;
  const netMins = grossMins !== null ? Math.max(0, grossMins - brkMins) : null;

  const pickUser = (id) => {
    const u = users.find((x) => x.id === id);
    setForm((f) => ({ ...f, user_id: id, site: f.site || u?.primary_site || "" }));
  };

  const submit = async () => {
    if (!form.user_id) { toast.error("Pick an employee"); return; }
    if (!form.site) { toast.error("Pick a site"); return; }
    if (!cIn || !cOut) { toast.error("Enter both clock in and clock out times"); return; }
    if (!form.reason.trim()) { toast.error("Reason is required for audit log"); return; }
    setBusy(true);
    try {
      await api.post("/admin/shifts", {
        user_id: form.user_id,
        site: form.site,
        clock_in: cIn.toISOString(),
        clock_out: cOut.toISOString(),
        break_minutes: form.break_minutes === "" ? null : brkMins,
        admin_note: form.admin_note || null,
        reason: form.reason.trim(),
      });
      toast.success("Shift added");
      setOpen(false);
      setForm(blank);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add this shift.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(blank); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#1A2744] text-white hover:bg-[#1A2744]/90" data-testid="add-shift-open">
          <Plus className="h-4 w-4 mr-1" /> Add shift
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add a shift manually</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
            For staff who forgot to clock in and out. The shift is saved as COMPLETE so it still goes through the normal approval queue before payroll.
          </div>
          <div>
            <Label>Employee</Label>
            <Select value={form.user_id} onValueChange={pickUser}>
              <SelectTrigger data-testid="add-shift-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} {u.employee_code ? `· ${u.employee_code}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Site</Label>
            <Select value={form.site} onValueChange={(v) => setForm({ ...form, site: v })}>
              <SelectTrigger data-testid="add-shift-site"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>
                {Object.keys(SITE_LABEL).map((code) => (
                  <SelectItem key={code} value={code}>{SITE_LABEL[code]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Shift date</Label>
            <Input
              type="date"
              value={form.shift_date}
              onChange={(e) => setForm({ ...form, shift_date: e.target.value })}
              data-testid="add-shift-date"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Clock in (24h)</Label>
              <TimeInput24
                value={form.clock_in_time}
                onChange={(v) => setForm({ ...form, clock_in_time: v })}
                data-testid="add-shift-clockin"
              />
            </div>
            <div>
              <Label>Clock out (24h)</Label>
              <TimeInput24
                value={form.clock_out_time}
                onChange={(v) => setForm({ ...form, clock_out_time: v })}
                data-testid="add-shift-clockout"
              />
            </div>
          </div>
          {overnight && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              Clock-out is before clock-in — saving as an overnight shift ending the next day.
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-600">Break (minutes)</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="number"
                min="0"
                step="5"
                className="w-28"
                placeholder="auto"
                value={form.break_minutes}
                onChange={(e) => setForm({ ...form, break_minutes: e.target.value })}
                data-testid="add-shift-break-minutes"
              />
              {[0, 15, 30, 45, 60].map((m) => (
                <Button
                  key={m}
                  type="button"
                  size="sm"
                  variant="outline"
                  className="px-2"
                  onClick={() => setForm({ ...form, break_minutes: String(m) })}
                  data-testid={`add-shift-break-preset-${m}`}
                >
                  {m === 0 ? "No break" : `${m}m`}
                </Button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              Leave blank to apply the automatic 30-min deduction on shifts over 6 hours.
            </p>
          </div>
          {netMins !== null && (
            <div className="rounded-md bg-white border border-slate-200 px-3 py-2 text-xs text-slate-700" data-testid="add-shift-preview">
              Gross {(grossMins / 60).toFixed(2)} hrs − break {brkMins} min ={" "}
              <span className="font-semibold">{(netMins / 60).toFixed(2)} hrs paid</span>
            </div>
          )}
          <div>
            <Label>Admin note (optional)</Label>
            <Input
              value={form.admin_note}
              onChange={(e) => setForm({ ...form, admin_note: e.target.value })}
              placeholder="e.g. Hours confirmed with duty manager"
              data-testid="add-shift-note"
            />
          </div>
          <div>
            <Label>Reason (required — written to audit log)</Label>
            <Input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Employee forgot to clock in and out"
              data-testid="add-shift-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="add-shift-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-[#1A2744] text-white"
            data-testid="add-shift-save"
          >
            {busy ? "Adding…" : "Add shift"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTimesheets() {
  const [shifts, setShifts] = useState([]);
  const [view, setView] = useState("weekly"); // "weekly" | "all"
  const [groupBy, setGroupBy] = useState("employee"); // "employee" | "date"
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [filter, setFilter] = useState({ site: "ALL", status_filter: "ALL", start: "", end: "" });

  const effectiveRange = useMemo(() => {
    if (view === "weekly") {
      return { start: isoDate(weekStart), end: isoDate(addDays(weekStart, 6)) };
    }
    return { start: filter.start, end: filter.end };
  }, [view, weekStart, filter.start, filter.end]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter.site !== "ALL") params.set("site", filter.site);
    if (filter.status_filter !== "ALL") params.set("status_filter", filter.status_filter);
    if (effectiveRange.start) params.set("start", effectiveRange.start);
    if (effectiveRange.end) params.set("end", effectiveRange.end);
    const { data } = await api.get(`/admin/shifts?${params}`);
    setShifts(data.shifts);
  }, [filter.site, filter.status_filter, effectiveRange.start, effectiveRange.end]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const pg = usePagination(shifts, 10);

  const approve = async (id) => {
    try { await api.post(`/admin/shifts/${id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  // ----- Build groups for the weekly view -----
  const groups = useMemo(() => {
    if (view !== "weekly") return [];
    const map = new Map();
    for (const s of shifts) {
      let key, label, sortKey;
      if (groupBy === "employee") {
        key = s.user_id || s.user_name || "unknown";
        label = s.user_name + (s.employee_code ? ` · ${s.employee_code}` : "");
        sortKey = (s.user_name || "").toLowerCase();
      } else {
        const d = new Date(s.clock_in_ts);
        const localDate = isoDate(d);
        key = localDate;
        label = new Date(s.clock_in_ts).toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
        sortKey = localDate;
      }
      if (!map.has(key)) map.set(key, { key, label, sortKey, shifts: [], totalMin: 0 });
      const g = map.get(key);
      g.shifts.push(s);
      g.totalMin += s.total_minutes || 0;
    }
    // Sort each group's shifts by clock-in
    for (const g of map.values()) {
      g.shifts.sort((a, b) => (a.clock_in_ts || "").localeCompare(b.clock_in_ts || ""));
    }
    return [...map.values()].sort((a, b) => (a.sortKey > b.sortKey ? 1 : -1));
  }, [shifts, groupBy, view]);

  const weekTotal = useMemo(
    () => shifts.reduce((acc, s) => acc + (s.total_minutes || 0), 0),
    [shifts]
  );

  return (
    <div className="space-y-6" data-testid="admin-timesheets-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">Timesheets</h1>
          <p className="text-gray-500 mt-1">Review, adjust and approve shifts before payroll.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AddShiftDialog onCreated={load} />
          <a href={`${API_BASE}/admin/shifts/export.csv`} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="export-shifts"><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
          </a>
        </div>
      </header>

      {/* View toggle + week navigator */}
      <Card>
        <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setView("weekly")}
                className={`px-3 py-1.5 text-sm ${view === "weekly" ? "bg-[#1A2744] text-white" : "bg-white text-[#1A2744]"}`}
                data-testid="view-weekly"
              >
                Weekly
              </button>
              <button
                onClick={() => setView("all")}
                className={`px-3 py-1.5 text-sm ${view === "all" ? "bg-[#1A2744] text-white" : "bg-white text-[#1A2744]"}`}
                data-testid="view-all"
              >
                All shifts
              </button>
            </div>
            {view === "weekly" && (
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden ml-2">
                <button
                  onClick={() => setGroupBy("employee")}
                  className={`px-3 py-1.5 text-xs ${groupBy === "employee" ? "bg-[#C9A84C]/20 text-[#1A2744] font-semibold" : "bg-white text-gray-600"}`}
                  data-testid="group-by-employee"
                >
                  By employee
                </button>
                <button
                  onClick={() => setGroupBy("date")}
                  className={`px-3 py-1.5 text-xs ${groupBy === "date" ? "bg-[#C9A84C]/20 text-[#1A2744] font-semibold" : "bg-white text-gray-600"}`}
                  data-testid="group-by-date"
                >
                  By date
                </button>
              </div>
            )}
          </div>

          {view === "weekly" && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm" variant="outline"
                onClick={() => setWeekStart(addDays(weekStart, -7))}
                data-testid="week-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm font-semibold text-[#1A2744] min-w-[160px] text-center" data-testid="week-label">
                {formatWeekLabel(weekStart)}
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => setWeekStart(addDays(weekStart, 7))}
                data-testid="week-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                size="sm" variant="ghost"
                onClick={() => setWeekStart(mondayOf(new Date()))}
                data-testid="week-this"
              >
                This week
              </Button>
              <div className="hidden md:block text-xs text-gray-500 border-l border-gray-200 pl-3 ml-1">
                Week total: <span className="font-semibold text-[#1A2744]">{(weekTotal / 60).toFixed(2)} hrs</span>
                <span className="mx-1">·</span>
                {shifts.length} shifts
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label>Site</Label>
            <Select value={filter.site} onValueChange={(v) => setFilter({ ...filter, site: v })}>
              <SelectTrigger data-testid="filter-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="SITE_1">Christchurch</SelectItem>
                <SelectItem value="SITE_2">Wellington</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={filter.status_filter} onValueChange={(v) => setFilter({ ...filter, status_filter: v })}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                {["ACTIVE", "COMPLETE", "APPROVED", "INCOMPLETE", "FLAGGED", "DISPUTED"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {view === "all" && (
            <>
              <div><Label>From</Label><Input type="date" value={filter.start} onChange={(e) => setFilter({ ...filter, start: e.target.value })} data-testid="filter-start" /></div>
              <div><Label>To</Label><Input type="date" value={filter.end} onChange={(e) => setFilter({ ...filter, end: e.target.value })} data-testid="filter-end" /></div>
            </>
          )}
          <Button variant="outline" onClick={() => setFilter({ site: "ALL", status_filter: "ALL", start: "", end: "" })}>Clear</Button>
        </CardContent>
      </Card>

      {view === "weekly" ? (
        <WeeklyGroupedView
          groups={groups}
          groupBy={groupBy}
          load={load}
          approve={approve}
        />
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <ShiftsTable shifts={pg.pageItems} onLoad={load} onApprove={approve} />
          </CardContent>
        </Card>
      )}

      {view === "all" && (
        <Pagination
          page={pg.page} pageCount={pg.pageCount} total={pg.total}
          pageSize={pg.pageSize} onPageChange={pg.setPage} idPrefix="admin-shifts"
        />
      )}
    </div>
  );
}

function WeeklyGroupedView({ groups, groupBy, load, approve }) {
  if (groups.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-sm text-gray-500">
        No shifts in the selected week.
      </CardContent></Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="weekly-grouped-view">
      {groups.map((g) => (
        <Card key={g.key} data-testid={`group-${g.key}`}>
          <CardContent className="p-0">
            <div className="px-4 py-3 flex items-center justify-between bg-[#1A2744]/[0.03] border-b border-gray-100">
              <div>
                <div className="font-semibold text-[#1A2744] text-sm">
                  {groupBy === "employee" ? "👤" : "📅"} {g.label}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">{g.shifts.length} shift{g.shifts.length === 1 ? "" : "s"}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Total</div>
                <div className="font-mono font-bold text-[#1A2744]">{(g.totalMin / 60).toFixed(2)} hrs</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <ShiftsTable shifts={g.shifts} onLoad={load} onApprove={approve} hideEmployee={groupBy === "employee"} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ShiftsTable({ shifts, onLoad, onApprove, hideEmployee }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {!hideEmployee && <TableHead>Employee</TableHead>}
          <TableHead>Site</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>In</TableHead>
          <TableHead>Out</TableHead>
          <TableHead>Hrs</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Sync</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {shifts.length === 0 && (
          <TableRow><TableCell colSpan={hideEmployee ? 8 : 9} className="text-center text-gray-500 py-8">No shifts match.</TableCell></TableRow>
        )}
        {shifts.map((s) => (
          <TableRow key={s.id}>
            {!hideEmployee && (
              <TableCell>
                <div className="text-sm font-medium text-[#1A2744]">{s.user_name}</div>
                <div className="text-[11px] text-gray-500">{s.employee_code}</div>
              </TableCell>
            )}
            <TableCell className="text-xs text-gray-500">{SITE_LABEL[s.site]}</TableCell>
            <TableCell>{new Date(s.clock_in_ts).toLocaleDateString()}</TableCell>
            <TableCell>{new Date(s.clock_in_ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}</TableCell>
            <TableCell>{s.clock_out_ts ? new Date(s.clock_out_ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</TableCell>
            <TableCell>
              {s.total_minutes ? (s.total_minutes / 60).toFixed(2) : "—"}
              {s.break_minutes > 0 && (
                <span
                  className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                  data-testid={`break-badge-${s.id}`}
                  title={`${s.break_manual_override ? "Admin-set" : "Auto-deducted"} ${s.break_minutes} min break (gross ${((s.gross_minutes || 0) / 60).toFixed(2)} hrs)`}
                >
                  -{s.break_minutes}m{s.break_manual_override ? "*" : ""}
                </span>
              )}
              {s.break_manual_override && !s.break_minutes && (
                <span
                  className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
                  data-testid={`nobreak-badge-${s.id}`}
                  title="Admin recorded no break taken"
                >
                  no break
                </span>
              )}
            </TableCell>
            <TableCell>
              <StatusPill status={s.status} />
              {s.manual_entry && (
                <span
                  className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                  data-testid={`manual-badge-${s.id}`}
                  title={`Added manually by ${s.manual_entry_by || "admin"}${s.manual_entry_reason ? ` — ${s.manual_entry_reason}` : ""}`}
                >
                  manual
                </span>
              )}
            </TableCell>
            <TableCell>
              <div className="flex flex-col gap-1 items-start">
                <StatusPill status={s.xero_sync_status || "NONE"} />
                {s.xero_timesheet_url && (
                  <a
                    href={s.xero_timesheet_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-[#C9A84C] hover:text-[#1A2744] hover:underline"
                    data-testid={`xero-link-${s.id}`}
                    title={`Xero timesheet ${s.xero_ref}`}
                  >
                    <ExternalLink className="h-3 w-3" /> View in Xero
                  </a>
                )}
              </div>
            </TableCell>
            <TableCell className="flex gap-1">
              <EditDialog shift={s} onSaved={onLoad} />
              {s.status !== "APPROVED" && s.clock_out_ts && (
                <Button size="sm" variant="outline" onClick={() => onApprove(s.id)} data-testid={`approve-${s.id}`}>
                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                </Button>
              )}
              <DeleteDialog shift={s} onDeleted={onLoad} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
